import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { getCrops, Crop } from '@next/arable-lib/crop';
import { ProvinceService } from '@next/arable-lib/crop-field';
import {
  CenterOnSelection,
  CoordinateSystem,
  GeoCalculationService,
  GeoCalculationServiceInjector,
  SaveActiveLayers,
  SetPopupEnabledStatus,
  SetRvoEnabled,
} from '@next/arable-lib/geo';
import { CoreActions } from '@next/core-lib';
import { AppInsightsService, AppInsightsServiceInjector } from '@next/core-lib/app-insights';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { LocaleService } from '@next/core-lib/i18n';
import {
  NotificationActions,
  NotificationConnectionStatus,
  NotificationService,
  NotificationServiceInjector,
} from '@next/core-lib/notification';
import { SessionActions } from '@next/core-lib/session';
import { WindowService, WindowServiceInjector } from '@next/core-lib/window';
import { ofType, Actions, createEffect } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { Feature as OlFeature } from 'ol';
import { Extent } from 'ol/extent';
import { GeoJSON } from 'ol/format';
import { Geometry } from 'ol/geom';
import { Observable, of, EMPTY, from, throwError, asyncScheduler, timer } from 'rxjs';
import {
  mergeMap,
  map,
  withLatestFrom,
  switchMap,
  concatMap,
  startWith,
  finalize,
  take,
  delay,
  catchError,
  observeOn,
  reduce,
  toArray,
  takeUntil,
} from 'rxjs/operators';
import * as CopyCropFieldToManureSpaceActions from 'src/app/copy-cropfields-to-manure-space/store/copy-cropfields-to-manure-space.actions';
import { InteractionType, Feature, ExternalSource } from 'src/app/geo/models';

import {
  GeoLayerDataService,
  GeoLayerDataServiceInjector,
} from 'src/app/geo/services/geoLayerData.service';
import {
  SetInteractionType,
  SetPreviousInteractionType,
} from 'src/app/geo/store/actions/map-context.actions';
import { getPreviousInteractionType } from 'src/app/geo/store/selectors/map-context.selectors';
import { SelectCustomerDialogComponent } from 'src/app/shared/components/select-customer-dialog/select-customer-dialog.component';
import { transformedCropFields } from 'src/app/shared/helpers/crop-field-helpers';
import { isGroundWaterPreservationWithNitrogenCuts } from 'src/app/shared/helpers/nutrient-polluted.helper';
import {
  CropField,
  SoilType,
  CropFieldProperties,
  SiteState,
  DivideValidation,
  CropFieldType,
  MultiSelectItem,
  AgroConnectExternalParty,
  AgroConnectCropField,
  RvoProvider,
  UpsertCropFieldsWithRegulatoryParcelResponse,
  ExternalCropFieldSources,
} from 'src/app/shared/models';
import { ValidateCropFieldGeometryResponse } from 'src/app/shared/models/validate-crop-field-geometry.model';
import { AgroConnectService } from 'src/app/shared/services/agroconnect.service';
import { CombineGeometryService } from 'src/app/shared/services/combine-geometry.service';
import { CropFieldService } from 'src/app/shared/services/crop-field.service';
import { DivideGeometryService } from 'src/app/shared/services/divide-geometry.service';
import { SyncService } from 'src/app/shared/services/sync.service';
import {
  openSoilAnalysesReport,
  getSoilAnalyses,
} from 'src/app/soil-analysis/store/soil-analysis.actions';
import {
  selectSoilAnalyses,
  selectDefaultSoilAnalyses,
  selectSoilAnalysisLoading,
} from 'src/app/soil-analysis/store/soil-analysis.selectors';
import { AppActionTypes } from 'src/app/store/actions';

import {
  getAvailableCropYears,
  getCropFieldType,
  getCropYear,
  getDefaultCropYear,
  getCropYears,
  selectCustomerProgress,
} from 'src/app/store/selectors';

import { Environment } from 'src/environments/environment.model';

import {
  DeselectAllFields,
  FeatureActionTypes,
  InteractionRvoSelected,
  FieldOverlapDrawn,
  InteractionLastYearsFeatureSelected,
  InteractionShapefileFieldSelected,
} from '../../../geo/store/actions/feature.actions';

import { isRvOProvider } from '../../../shared/helpers/is-rvo-provider';
import { CropFieldDialogComponent } from '../../components/cropfield-dialog/cropfield-dialog.component';
import { EditMachiningDepthDialogComponent } from '../../components/edit-machining-depth-dialog/edit-machining-depth-dialog.component';
import { ImportCropfieldsDialogComponent } from '../../components/import-cropfields-dialog/import-cropfields-dialog.component';
import { QuickEditDialogComponent } from '../../components/quick-edit-dialog/quick-edit-dialog.component';
import { RetrieveCropfieldsDialogComponent } from '../../components/retrieve-cropfields-dialog/retrieve-cropfields-dialog.component';
import { SyncCropfieldsDialogComponent } from '../../components/sync-cropfields-dialog/sync-cropfields-dialog.component';
import { SyncCropfieldsDialogV2Component } from '../../components/sync-cropfields-dialog-v2/sync-cropfields-dialog-v2.component';
import { SyncCropfieldsFailedDialogComponent } from '../../components/sync-cropfields-failed-dialog/sync-cropfields-failed-dialog.component';
import { FailedToSyncCropFields } from '../../models';
import {
  CropFieldActionTypes,
  LoadCropFieldsSuccess,
  DeleteCropFieldRequest,
  DeleteCropFieldSuccess,
  UpsertCropFieldRequest,
  UpsertCropFieldSuccess,
  AddCropField,
  EditCropFields,
  LoadCropFieldsRequest,
  DrawFieldOverlap,
  LoadLastYearsCropFieldsSuccess,
  DeleteOriginalFields,
  PrefillCropField,
  SaveMachiningDepth,
  UpdateMachiningDepthSuccess,
  CopyFieldsToManureSpace,
  LoadMostUsedCropsRequest,
  LoadMostUsedCropsSuccess,
  UpsertImportedCropFieldsRequest,
  UpsertImportedCropFieldsSuccess,
  LoadAgroConnectCustomersRequest,
  LoadAgroConnectCustomersSuccess,
  LoadLastYearsCropFieldsRequest,
  SelectCropYearForImportFromCustomer,
  RetrieveCropFieldsFromCustomerForImport,
  LoadCropFieldsFromCustomerSuccess,
  SelectCropFieldsForImportFromCustomer,
  AskImportCropFieldsForRvO,
  LoadRvOCropFieldsSuccess,
  AskImportCropFieldsFromSync,
  AskImportCropFields,
  TriggerSyncCropFieldsV2Request,
  TriggerSyncCropFieldsV2Failed,
  SyncCropFieldsReadyReceived,
  LoadCropFieldsSyncPreviewV2Success,
  CompleteSyncCropFieldsV2Request,
  AskImportRegulatoryManureFields,
  AskSynchronizeCropFieldsRvO,
  AskSynchronizeCropFields,
  SyncCropFieldsReadySubmitted,
  IsAwaitingCropFieldsSync,
  SetCropFieldsSyncId,
} from '../actions/crop-field.actions';
import {
  getSelectedCropFieldsIds,
  getSelectedCropFields,
  getDetailedCropFields,
  isAwaitingSync,
  getSyncId,
} from '../selectors/crop-field.selectors';
import { getMostUsedCrops } from '../selectors/most-used-crops.selectors';
import { getSoilTypes } from '../selectors/soil-type.selectors';

@Injectable()
export class CropFieldEffects {
  private readonly actions$ = inject(Actions);
  private readonly fieldService = inject(CropFieldService);
  private readonly divideGeometryService = inject(DivideGeometryService);
  private readonly combineGeometryService = inject(CombineGeometryService);
  private readonly provinceService = inject(ProvinceService);
  private readonly agroConnectService = inject(AgroConnectService);
  private readonly syncService = inject(SyncService);
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly dialog = inject(DialogService);
  private readonly locale = inject(LocaleService);
  private readonly geoCalculationService = inject<GeoCalculationService>(
    GeoCalculationServiceInjector,
  );
  private readonly env = inject<Environment>(EnvironmentInjector);
  private readonly notification = inject<NotificationService>(NotificationServiceInjector);
  private readonly window = inject<WindowService>(WindowServiceInjector);
  private readonly layerDataService = inject<GeoLayerDataService>(GeoLayerDataServiceInjector);
  private readonly appInsights = inject<AppInsightsService>(AppInsightsServiceInjector);

  loadCropFields$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CropFieldActionTypes.LoadCropFieldsRequest),
      withLatestFrom(
        this.store.pipe(select(getCropFieldType)),
        this.store.pipe(select(getCropYear)),
      ),
      mergeMap(
        ([action, cropFieldType, cropYear]: [LoadCropFieldsRequest, CropFieldType, number]) =>
          this.fieldService.getCropFields(cropYear, cropFieldType).pipe(
            mergeMap((cropFields) => {
              let result: Action[] = [
                new LoadCropFieldsSuccess(
                  transformedCropFields(
                    cropFields,
                    CoordinateSystem.WorldGeodetic,
                    CoordinateSystem.PseudoMercator,
                  ),
                ),
              ];

              if (action.centerMap) {
                result = [
                  ...result,
                  cropFields.length > 0
                    ? new CenterOnSelection()
                    : new LoadLastYearsCropFieldsRequest(true),
                ];
              }

              return result;
            }),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          ),
      ),
    ),
  );

  loadLastYearsCropFields$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CropFieldActionTypes.LoadLastYearsCropFieldsRequest),
      withLatestFrom(
        this.store.pipe(select(getCropFieldType)),
        this.store.pipe(select(getCropYear)),
      ),
      mergeMap(
        ([action, cropFieldType, cropYear]: [
          LoadLastYearsCropFieldsRequest,
          CropFieldType,
          number,
        ]) =>
          this.fieldService.getCropFields(cropYear - 1, cropFieldType).pipe(
            mergeMap((cropFields) => {
              let result: Action[] = [
                new LoadLastYearsCropFieldsSuccess(
                  transformedCropFields(
                    cropFields,
                    CoordinateSystem.WorldGeodetic,
                    CoordinateSystem.PseudoMercator,
                  ),
                ),
              ];

              if (action.centerMap) {
                result = [...result, new CenterOnSelection()];
              }

              return result;
            }),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          ),
      ),
    ),
  );

  deleteCropFields$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CropFieldActionTypes.DeleteCropFields),
      withLatestFrom(this.store.pipe(select(getSelectedCropFields))),
      switchMap(([_, cropFields]) => {
        const hasSelectedTgaParticipatingCropFields = cropFields.some(
          (cropField) => cropField.isParticipatingToTga === true,
        );
        let dialogText = this.locale.translate(
          cropFields.length > 1 ? 'removeCropFields' : 'removeCropField',
        );

        if (hasSelectedTgaParticipatingCropFields) {
          dialogText = this.locale.translate(
            cropFields.length > 1
              ? 'cropFieldsWithTgaAdviceDeleteWarning'
              : 'cropFieldWithTgaAdviceDeleteWarning',
          );
        }

        return this.dialog
          .confirm({
            title: this.locale.translate('remove'),
            text: dialogText,
          })
          .pipe(
            switchMap(({ status }) =>
              status === DIALOG_RETURN_TYPES.confirm
                ? cropFields.map((field) => new DeleteCropFieldRequest(field.id))
                : EMPTY,
            ),
          );
      }),
    ),
  );

  deleteCropField$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CropFieldActionTypes.DeleteCropFieldRequest),
      map((action: DeleteCropFieldRequest) => action.id),
      mergeMap((id) =>
        this.fieldService.deleteCropField(id).pipe(
          map(() => new DeleteCropFieldSuccess(id)),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  upsertCropField$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CropFieldActionTypes.UpsertCropFieldRequest),
      withLatestFrom(this.store.pipe(select(getPreviousInteractionType))),
      mergeMap(([action, interactiontype]: [UpsertCropFieldRequest, InteractionType]) => {
        const cropFieldToUpsert = transformedCropFields(
          [action.cropField],
          CoordinateSystem.PseudoMercator,
          CoordinateSystem.WorldGeodetic,
        )[0];
        return this.fieldService.upsertCropField(cropFieldToUpsert).pipe(
          concatMap((cropField: CropField) => {
            if (cropField.id !== cropFieldToUpsert.id) {
              this.appInsights.trackEvent(`CropFieldAdded.${InteractionType[interactiontype]}`, {});
            }
            return [
              new UpsertCropFieldSuccess(
                {
                  ...action.cropField,
                  ...cropField,
                },
                cropField.id !== cropFieldToUpsert.id,
              ),
              new DeselectAllFields(),
              new LoadCropFieldsRequest(),
              getSoilAnalyses(),
            ];
          }),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => {
            const options = action.createOptions ? action.createOptions : {};
            this.store.dispatch(new LoadMostUsedCropsRequest());

            if (options.nextCropFeatures && options.nextCropFeatures.length) {
              this.store.dispatch(
                new AddCropField(options.nextCropFeatures[0], {
                  nextCropFeatures: options.nextCropFeatures.slice(1),
                  originalCropFieldsToDelete: options.originalCropFieldsToDelete,
                }),
              );
            } else if (options.originalCropFieldsToDelete) {
              this.store.dispatch(new SetInteractionType(InteractionType.selectCropField));
              this.store.dispatch(new DeleteOriginalFields(options.originalCropFieldsToDelete));
            } else if (options.addAnotherField) {
              this.store.dispatch(new SetInteractionType(interactiontype));
            } else if (options.linkSoilAnalysisOrigin) {
              this.store.dispatch(new SetInteractionType(InteractionType.selectSoilAnalysis));
            } else {
              this.store.dispatch(new SetInteractionType(InteractionType.selectCropField));
            }

            return this.store.dispatch(CoreActions.setLoading({ loading: false }));
          }),
        );
      }),
    ),
  );

  deleteOriginalFields$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CropFieldActionTypes.DeleteOriginalFields),
        concatMap(({ cropFields }: DeleteOriginalFields) => {
          const translationKey =
            cropFields.length === 1 ? 'deleteOriginalField' : 'deleteOriginalFields';
          return this.dialog
            .confirm({
              title: this.locale.translate(`${translationKey}Title`),
              text: this.locale.translate(`${translationKey}Description`),
            })
            .pipe(
              switchMap(({ status }) => {
                if (status === DIALOG_RETURN_TYPES.confirm) {
                  cropFields.map((cropField) =>
                    this.store.dispatch(new DeleteCropFieldRequest(cropField.id)),
                  );
                }
                return EMPTY;
              }),
            );
        }),
      ),
    { dispatch: false },
  );

  addOrEditCropField$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CropFieldActionTypes.AddOrEditCropField),
      withLatestFrom(
        this.store.pipe(select(getSelectedCropFields)),
        this.store.pipe(select(getCropFieldType)),
      ),
      switchMap(([_, cropFields, cropFieldType]) => {
        if (cropFields.length > 0) {
          return of(new SetInteractionType(InteractionType.modifyFeature));
        } else {
          const dialogOptions = [
            {
              name: this.locale.translate('cloneRvoField'),
              value: InteractionType.cloneRvo,
              icon: 'file_copy',
            },
            {
              name: this.locale.translate('cloneFieldFromLastYear'),
              value: InteractionType.cloneFieldFromLastYear,
              icon: 'refresh',
            },
            {
              name: this.locale.translate('synchronizeCropFieldsWithBMS'),
              value: InteractionType.synchronizeCropFieldsWithBMS,
              icon: 'save_alt',
            },
            {
              name: this.locale.translate('importCropFieldsFromCustomer'),
              value: InteractionType.importCropFieldsFromCustomer,
              icon: 'supervisor_account',
            },
            {
              name: this.locale.translate('drawCropField'),
              value: InteractionType.drawFeature,
              icon: 'edit',
            },
          ];

          if (
            this.env.ENABLE_REGULATORY_MANURE_FIELDS_IMPORT &&
            cropFieldType === CropFieldType.ManureSpace
          ) {
            dialogOptions.splice(3, 0, {
              name: this.locale.translate('importRegulatoryManureFields'),
              value: InteractionType.importRegulatoryManureFields,
              icon: 'pets',
            });
          }

          if (this.env.ENABLE_RVO_SYNC && cropFieldType !== CropFieldType.ManureSpace) {
            dialogOptions.splice(3, 0, {
              name: this.locale.translate('synchronizeCropFieldsWithRvO'),
              value: InteractionType.synchronizeCropFieldsWithRvO,
              icon: 'save_alt',
            });
          }
          return this.dialog
            .actions({
              options: dialogOptions,
              pushHistoryState: false,
            })
            .pipe(
              switchMap(({ status, result }) => {
                const confirmed = status === DIALOG_RETURN_TYPES.confirm;
                if (!confirmed) {
                  return EMPTY;
                }

                this.appInsights.trackEvent(
                  `AddCropFieldDialogOption.${InteractionType[result]}`,
                  {},
                );
                this.store.dispatch(new SaveActiveLayers());
                this.store.dispatch(new SetPreviousInteractionType(result));
                return of(new SetInteractionType(result));
              }),
            );
        }
      }),
    ),
  );

  showCopyFieldsToManureSpaceDialog$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.ShowCopyFieldsToManureSpaceDialog),
      switchMap((_) =>
        this.dialog
          .confirm({
            title: this.locale.translate('copyFieldsFromCultivationPlan'),
            text: this.locale.translate('copyCropFieldsToManureSpaceDescription'),
          })
          .pipe(
            switchMap(({ status }) =>
              status === DIALOG_RETURN_TYPES.confirm ? of(new CopyFieldsToManureSpace()) : EMPTY,
            ),
          ),
      ),
    ),
  );

  copyCropFieldsToManureSpace$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CropFieldActionTypes.CopyCropFieldsToManureSpace),
      withLatestFrom(this.store.select(getCropYear)),
      switchMap(([_, cropYear]) =>
        this.fieldService.copyCropFieldsToManureSpace(cropYear).pipe(
          withLatestFrom(this.notification.status$),
          switchMap(([response, status]) => {
            if (response.amountOfCropFields === 0) {
              this.dialog.notify({
                title: this.locale.translate('copyCropFieldsToManureSpace.nothingToCopyTitle'),
                text: this.locale.translate('copyCropFieldsToManureSpace.nothingToCopyText'),
              });
            }
            if (
              status === NotificationConnectionStatus.Connecting ||
              status === NotificationConnectionStatus.Idle ||
              status === NotificationConnectionStatus.Retrying
            ) {
              this.dialog
                .notify({
                  title: this.locale.translate('copyCropFieldsToManureSpace.manualRefreshTitle'),
                  text: this.locale.translate('copyCropFieldsToManureSpace.manualRefreshText'),
                })
                .subscribe(() => {
                  this.window.reload();
                });
            }
            return EMPTY;
          }),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  cloneRvoFields$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FeatureActionTypes.InteractionRvoSelected),
      switchMap((action: InteractionRvoSelected) => {
        this.store.dispatch(new PrefillCropField(true));
        return of(new AddCropField(action.feature, { closeRvoLayer: true }));
      }),
    ),
  );

  cloneLastYearsField$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FeatureActionTypes.InteractionLastYearsFeatureSelected),
      switchMap((action: InteractionLastYearsFeatureSelected) => {
        const feature: Feature = JSON.parse(JSON.stringify(action.feature)) as Feature;
        const s = new Date(feature.properties?.startDate);
        const e = new Date(feature.properties?.endDate);
        const newStartDate = !s ? null : new Date(s.getFullYear() + 1, s.getMonth(), s.getDate());
        const newEndDate = !e ? null : new Date(e.getFullYear() + 1, e.getMonth(), e.getDate());

        if (newStartDate && newEndDate) {
          feature.properties.startDate = `${newStartDate.toJSON().split('T')[0]}T00:00:00Z`;
          feature.properties.endDate = `${newEndDate.toJSON().split('T')[0]}T00:00:00Z`;
        }
        feature.properties.cropVarietyId = undefined;
        feature.properties.cropId = undefined;

        return of(new AddCropField(feature));
      }),
    ),
  );

  cloneShapefileField$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FeatureActionTypes.InteractionShapefileFieldSelected),
      switchMap((action: InteractionShapefileFieldSelected) => {
        this.store.dispatch(new PrefillCropField(true));
        return of(new AddCropField(action.feature));
      }),
    ),
  );

  cloneCropFields$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CropFieldActionTypes.CloneSelectedCropField),
      withLatestFrom(this.store.pipe(select(getSelectedCropFields))),
      concatMap(([_, cropFields]) => {
        if (cropFields.length > 0) {
          const cropField: CropField = {
            ...cropFields[0],
            id: 0,
            cropId: null,
            cropVarietyId: null,
            endDate: null,
            startDate: null,
          };
          return [new EditCropFields([cropField])];
        }
        return EMPTY;
      }),
    ),
  );

  addCropField$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CropFieldActionTypes.AddCropField),
      withLatestFrom(this.store.pipe(select(getCropFieldType))),
      map(([action, cropFieldType]: [AddCropField, CropFieldType]) => {
        const properties: Partial<CropFieldProperties> = action.feature.properties
          ? action.feature.properties
          : {};
        const calculatedAreaInHectares = this.geoCalculationService.getAreaInHectares(
          action.feature,
        );
        const areaInHectaresFromProperties = properties.areaInHectares
          ? properties.areaInHectares
          : calculatedAreaInHectares;

        const isAreaCalculated =
          Math.round(calculatedAreaInHectares * 100) / 100 ===
          Math.round(areaInHectaresFromProperties * 100) / 100;

        const cropField: CropField = {
          id: 0,
          cropFieldType,
          cropId: properties.cropId || null,
          cropVarietyId: properties.cropVarietyId || null,
          soilTypeId: properties.soilTypeId || null,
          name: properties.name || '',
          areaInHectares: isAreaCalculated
            ? calculatedAreaInHectares
            : areaInHectaresFromProperties,
          isAreaCalculated,
          feature: action.feature,
          isSkal: !!properties.isSkal,
          isPlanetProof: !!properties.isPlanetProof,
          isGroundWaterPreservation: !!properties.isGroundWaterPreservation,
          isSandySoil: !!properties.isSandySoil,
          isRentedField: !!properties.isRentedField,
          startDate: properties.startDate || null,
          endDate: properties.endDate || null,
          soilAnalysisIds: properties.soilAnalysisIds || [],
          outsideDutchBorders: !!properties.outsideDutchBorders,
          province: properties.province || null,
          machiningDepthInCentimeters: properties.machiningDepthInCentimeters || 25,
          isSubsequentCultivation: properties.isSubsequentCultivation || false,
          originalCropFieldId: null,
          originalCropFieldIdForSubsequentCultivation:
            properties.originalCropFieldIdForSubsequentCultivation || null,
          isNutrientsPollutedArea: properties.isNutrientsPollutedArea || false,
          isGroundWaterPreservationWithNitrogenCuts:
            properties.isGroundWaterPreservationWithNitrogenCuts || false,
        };
        return { cropFields: [cropField], createOptions: action.createOptions };
      }),
      concatMap(({ cropFields, createOptions }) => [
        new EditCropFields(
          cropFields,
          {
            showAddAnotherButton: !(
              createOptions &&
              (createOptions.nextCropFeatures || createOptions.addAnotherField === false)
            ),
          },
          {
            ...createOptions,
          },
        ),
      ]),
    ),
  );

  editCropFields$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CropFieldActionTypes.EditCropFields),
      withLatestFrom(
        this.store.pipe(select(getCrops)),
        this.store.pipe(select(getMostUsedCrops)),
        this.store.pipe(select(getSoilTypes)),
        this.store.pipe(select(getCropYear)),
      ),
      concatMap(
        ([action, crops, mostUsedCrops, soilTypes, cropYear]: [
          EditCropFields,
          Crop[],
          Crop[],
          SoilType[],
          number,
        ]) =>
          this.dialog
            .form(CropFieldDialogComponent, {
              title: this.locale.translate('cropFieldProperties'),
              cropField: action.cropFields[0],
              crops,
              mostUsedCrops,
              soilTypes,
              cropYear,
              isSubsequentCultivation:
                action.editOptions && action.editOptions.isSubsequentCultivation,
              fullscreen: true,
              confirmButtonText:
                action.editOptions && action.editOptions.showAddAnotherButton
                  ? this.locale.translate('saveAndAddAnotherField')
                  : this.locale.translate('save'),
              pushHistoryState: false,
              altButton: action.editOptions && action.editOptions.showAddAnotherButton,
              altButtonColor: 'primary',
              altButtonIcon: 'save',
              altButtonText: this.locale.translate('save'),
              closeRvoLayer: false,
            })
            .pipe(
              concatMap(({ status, result }) => {
                if (action.createOptions && action.createOptions.closeRvoLayer === true) {
                  this.store.dispatch(new SetRvoEnabled(false));
                }
                switch (status) {
                  case DIALOG_RETURN_TYPES.confirm:
                    if (action.editOptions && action.editOptions.showAddAnotherButton) {
                      return [
                        new SetPopupEnabledStatus(true),
                        new UpsertCropFieldRequest(result as CropField, {
                          addAnotherField: true,
                        }),
                      ];
                    } else {
                      return [
                        new SetPopupEnabledStatus(true),
                        new UpsertCropFieldRequest(result as CropField, {
                          ...action.createOptions,
                        }),
                      ];
                    }
                  case DIALOG_RETURN_TYPES.alt:
                    return [
                      new SetPopupEnabledStatus(true),
                      new UpsertCropFieldRequest(result as CropField, {
                        ...action.createOptions,
                      }),
                    ];
                  default:
                    return [
                      new SetPopupEnabledStatus(true),
                      new SetInteractionType(InteractionType.selectCropField),
                    ];
                }
              }),
            ),
      ),
    ),
  );

  editSelectedCropField$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CropFieldActionTypes.EditSelectedCropFields),
      withLatestFrom(this.store.pipe(select(getSelectedCropFields))),
      concatMap(([_, cropFields]) => [
        new EditCropFields([
          {
            ...cropFields[0],
          },
        ]),
      ]),
    ),
  );

  sendSelectedCropFields$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CropFieldActionTypes.SendSelectedCropFields),
      withLatestFrom(
        this.store.pipe(
          select(getSelectedCropFields),
          map((cropFields) => JSON.stringify(cropFields.map((cropField) => cropField.id))),
        ),
      ),
      map(([_, ids]) =>
        SessionActions.windowOpenWithSession({
          url: `${this.env.FIELD_ADVICE_URL}/cropfields/${ids}`,
          name: '_self',
        }),
      ),
    ),
  );

  createSubsequentCultivation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CropFieldActionTypes.CreateSubsequentCultivation),
      withLatestFrom(this.store.pipe(select(getSelectedCropFields))),
      concatMap(([_, cropFields]) => {
        if (cropFields.length > 0) {
          const cropField: CropField = {
            ...cropFields[0],
            id: 0,
            cropId: null,
            cropVarietyId: null,
            endDate: null,
            startDate: cropFields[0].endDate,
            originalCropFieldIdForSubsequentCultivation: cropFields[0].id,
          };
          return [new EditCropFields([cropField], { isSubsequentCultivation: true })];
        }
        return EMPTY;
      }),
    ),
  );

  divideField$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CropFieldActionTypes.DivideField),
      withLatestFrom(this.store.pipe(select(getSelectedCropFields))),
      concatMap(([_]) => of(new SetInteractionType(InteractionType.drawDivision))),
    ),
  );

  fieldDivisionDrawn$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FeatureActionTypes.FieldDivisionDrawn),
        withLatestFrom(this.store.pipe(select(getSelectedCropFields))),
        concatMap(([action, cropFields]: [FieldOverlapDrawn, CropField[]]) => {
          if (cropFields.length !== 1 || !(cropFields[0] && cropFields[0].feature)) {
            this.dialog.notify({
              title: this.locale.translate('splitFieldFailed'),
              text: this.locale.translate('splittingRequiresOneField'),
            });
            return EMPTY;
          }
          const result = this.divideGeometryService.divide(cropFields[0].feature, action.feature);

          switch (result.validation) {
            case DivideValidation.invalidSelection:
              this.store.dispatch(
                NotificationActions.warningSnackbar({
                  message: this.locale.translate('splittingInvalidSelection'),
                }),
              );
              return EMPTY;
            case DivideValidation.notIntersecting:
            case DivideValidation.notProducingTwoFields:
              this.store.dispatch(
                NotificationActions.warningSnackbar({
                  message: this.locale.translate('splittingRequiresPartialOverlap'),
                }),
              );
              return EMPTY;
            case DivideValidation.wrongNumberOfGeometries:
              this.store.dispatch(
                NotificationActions.warningSnackbar({
                  message: this.locale.translate('splittingCanOnlyResultInTwoParts'),
                }),
              );
              return EMPTY;
          }

          const newFeatures = this.divideGeometryService.getFeatures(result);

          this.store.dispatch(new SetPreviousInteractionType(InteractionType.drawFeature));

          const newProperties: CropField = {
            ...cropFields[0],
            areaInHectares: null,
            cropId: null,
            cropVarietyId: null,
            startDate: null,
            endDate: null,
            layerData: null,
            name: null,
          };
          delete newProperties.feature;
          delete newProperties.id;
          delete newProperties.isAreaCalculated;
          delete newProperties.cropFieldType;
          newFeatures.feature1.properties = newFeatures.feature2.properties = newProperties;

          this.store.dispatch(
            new AddCropField(newFeatures.feature1, {
              nextCropFeatures: [newFeatures.feature2],
              originalCropFieldsToDelete: cropFields,
            }),
          );
          return EMPTY;
        }),
      ),
    { dispatch: false },
  );

  combineFields$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CropFieldActionTypes.CombineFields),
        withLatestFrom(this.store.pipe(select(getSelectedCropFields))),
        concatMap(([_, fields]) => {
          if (
            fields.length !== 2 ||
            !(fields[0] && fields[0].feature) ||
            !(fields[1] && fields[1].feature)
          ) {
            this.dialog.notify({
              title: this.locale.translate('combineFieldsFailed'),
              text: this.locale.translate('combiningOnlyWorksWithTwoFields'),
            });
            return EMPTY;
          }

          const result = this.combineGeometryService.prepare(fields[0].feature, fields[1].feature);

          if (!result.isIntersecting) {
            this.dialog
              .confirm({
                title: this.locale.translate('combineFieldsFailed'),
                text: this.locale.translate('fieldsDontIntersectEachOther'),
                confirmButtonText: this.locale.translate('drawOverlap'),
              })
              .pipe(
                take(1),
                switchMap(({ status }) => {
                  if (status === DIALOG_RETURN_TYPES.confirm) {
                    this.store.dispatch(new DrawFieldOverlap());
                  }
                  return EMPTY;
                }),
              )
              .subscribe();
            return EMPTY;
          }

          const newFeature = this.combineGeometryService.merge(result);
          newFeature.properties = this.combineCropFieldProperties(fields[0], fields[1]);

          this.store.dispatch(new SetInteractionType(InteractionType.selectCropField));
          this.store.dispatch(
            new AddCropField(newFeature, {
              addAnotherField: false,
              originalCropFieldsToDelete: fields,
            }),
          );
          return EMPTY;
        }),
      ),
    { dispatch: false },
  );

  drawFieldOverlap$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CropFieldActionTypes.DrawFieldOverlap),
      concatMap(() => of(new SetInteractionType(InteractionType.drawOverlap))),
    ),
  );

  completedCopyCropFieldsToManureSpace$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CopyCropFieldToManureSpaceActions.completedCopyAll),
      concatMap(() => of(new LoadCropFieldsRequest())),
    ),
  );

  fieldOverlapDrawn$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FeatureActionTypes.FieldOverlapDrawn),
      withLatestFrom(this.store.pipe(select(getSelectedCropFields))),
      concatMap(([action, fields]: [FieldOverlapDrawn, CropField[]]) => {
        const resultPart = this.combineGeometryService.prepare(fields[0].feature, action.feature);
        let newFeaturePart: Feature = null;
        let newFeatureComplete: Feature = null;

        if (!resultPart.success) {
          this.store.dispatch(
            NotificationActions.warningSnackbar({
              message: this.locale.translate('couldntCreateFieldFromCurrentSelection'),
            }),
          );
          return EMPTY;
        }

        try {
          newFeaturePart = this.combineGeometryService.merge(resultPart);
        } catch (_error) {
          console.error('Error while merging geometries:', _error);
          this.store.dispatch(
            NotificationActions.warningSnackbar({
              message: this.locale.translate('couldntCreateFieldFromCurrentSelection'),
            }),
          );
          return EMPTY;
        }

        const resultComplete = this.combineGeometryService.prepare(
          fields[1].feature,
          newFeaturePart,
        );

        if (!resultComplete.success) {
          this.store.dispatch(
            NotificationActions.warningSnackbar({
              message: this.locale.translate('couldntCreateFieldFromCurrentSelection'),
            }),
          );
          return EMPTY;
        }

        try {
          newFeatureComplete = this.combineGeometryService.merge(resultComplete);
        } catch (_error) {
          console.error('Error while merging geometries:', _error);
          this.store.dispatch(
            NotificationActions.warningSnackbar({
              message: this.locale.translate('couldntCreateFieldFromCurrentSelection'),
            }),
          );
          return EMPTY;
        }

        this.store.dispatch(new SetPreviousInteractionType(InteractionType.drawFeature));

        newFeatureComplete.properties = this.combineCropFieldProperties(fields[0], fields[1]);

        return of(
          new AddCropField(newFeatureComplete, {
            addAnotherField: false,
            originalCropFieldsToDelete: fields,
          }),
        );
      }),
    ),
  );

  openSoilAnalysesReportForSelectedCropFields$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CropFieldActionTypes.OpenSoilAnalysesReportForSelectedCropFields),
      withLatestFrom(
        this.store.pipe(select(getSelectedCropFields)),
        this.store.pipe(select(selectSoilAnalyses)),
        this.store.pipe(select(selectDefaultSoilAnalyses)),
      ),
      concatMap(([_, cropFields, soilAnalyses, defaultSoilAnalysis]) => {
        const uniqueCharacteristics: MultiSelectItem[] = [];
        const foundCharacteristics: MultiSelectItem[] = []
          .concat(
            soilAnalyses.concat(defaultSoilAnalysis).map((analysis) =>
              analysis?.sampledCharacteristics.map((characteristic) => ({
                name: characteristic?.designation,
                value: characteristic?.id,
              })),
            ),
          )
          .reduce((acc, val) => acc.concat(val), []);
        foundCharacteristics.forEach((characteristic) => {
          if (!uniqueCharacteristics.find((char) => char.value === characteristic.value)) {
            uniqueCharacteristics.push(characteristic);
          }
        });
        return this.dialog
          .multiselect({
            options: uniqueCharacteristics.sort((a, b) =>
              a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1,
            ),
            title: this.locale.translate('selectReportValues'),
            selected: [],
            emptyText: '',
          })
          .pipe(
            delay(100),
            switchMap(({ status, result }) => {
              if (status === DIALOG_RETURN_TYPES.confirm) {
                return [
                  openSoilAnalysesReport({
                    cropFieldIds: cropFields.map((cropField) => cropField.id),
                    characteristicIds: result,
                  }),
                ];
              }
              return EMPTY;
            }),
          );
      }),
    ),
  );

  editMachiningDepth$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CropFieldActionTypes.EditMachiningDepth),
      withLatestFrom(this.store.pipe(select(getSelectedCropFieldsIds))),
      switchMap(([_, cropFieldIds]) =>
        this.dialog
          .form(EditMachiningDepthDialogComponent, {
            title: this.locale.translate('editMachiningDepth'),
          })
          .pipe(
            concatMap(({ status, result }) =>
              status === DIALOG_RETURN_TYPES.confirm
                ? [
                    ...cropFieldIds.map(
                      (id) => new SaveMachiningDepth(id, result.machiningDepthInCentimeters),
                    ),
                    new DeselectAllFields(),
                  ]
                : EMPTY,
            ),
          ),
      ),
    ),
  );

  saveMachiningDepth$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CropFieldActionTypes.SaveMachiningDepth),
      mergeMap(({ id, machiningDepthInCentimeters }) =>
        this.fieldService.updateMachiningDepth(id, machiningDepthInCentimeters).pipe(
          map((_) => new UpdateMachiningDepthSuccess(id, machiningDepthInCentimeters)),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  quickEditCropFields$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CropFieldActionTypes.QuickEditCropFields),
      withLatestFrom(
        this.store.pipe(select(getDetailedCropFields)),
        this.store.pipe(select(getCrops)),
        this.store.pipe(select(getMostUsedCrops)),
        this.store.pipe(select(getSoilTypes)),
        this.store.pipe(select(getCropYear)),
      ),
      switchMap(([_, cropFields, crops, mostUsedCrops, soilTypes, cropYear]) =>
        this.dialog
          .form(
            QuickEditDialogComponent,
            {
              title: this.locale.translate('quickEditFields'),
              cropFields,
              crops,
              mostUsedCrops,
              soilTypes,
              cropYear,
              isSubsequentCultivation: false,
              fullscreen: true,
              confirmButton: true,
            },
            { maxWidth: '90vw', width: '100%' },
          )
          .pipe(
            switchMap(({ status, result }) => {
              const actions =
                status === DIALOG_RETURN_TYPES.confirm && result
                  ? result.editedCropFields
                      .map((f) => new UpsertCropFieldRequest(f, {}) as Action)
                      .concat(
                        result.deletedCropFields.map(
                          (f) => new DeleteCropFieldRequest(f.id) as Action,
                        ),
                      )
                  : EMPTY;

              return actions;
            }),
          ),
      ),
    ),
  );

  loadMostUsedCrops$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CropFieldActionTypes.LoadMostUsedCropsRequest),
      withLatestFrom(this.store.pipe(select(getCropFieldType)), this.store.pipe(select(getCrops))),
      mergeMap(([_, cropFieldType, crops]) =>
        this.fieldService.getMostUsedCropReferences(cropFieldType).pipe(
          map((mostUsedCropReferences) => {
            const mostUsedCrops = mostUsedCropReferences
              .map((r) => crops.find((c) => c.id === r.id))
              .filter((c) => c != null);
            return new LoadMostUsedCropsSuccess(mostUsedCrops);
          }),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  askSynchronizeCropfields$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CropFieldActionTypes.AskSynchronizeCropFields),
      withLatestFrom(
        this.store.pipe(select(getCropYears)),
        this.store.pipe(select(getCropFieldType)),
      ),
      switchMap(([_, cropYears, cropFieldType]) =>
        this.dialog
          .dialog(
            RetrieveCropfieldsDialogComponent,
            {
              title: this.locale.translate('synchronizeCropFieldsWithBMS'),
              fullscreen: true,
              confirmButton: true,
              cancelButton: true,
              cancelButtonText: this.locale.translate('cancel'),
              providers: Object.values(AgroConnectExternalParty),
              years: cropYears,
            },
            { maxWidth: 500 },
          )
          .pipe(
            switchMap(({ status, result }) => {
              if (status === DIALOG_RETURN_TYPES.confirm) {
                this.appInsights.trackEvent(`BmsImportDialog.${result.provider.toString()}`, {});

                let externalCropFieldSource: ExternalCropFieldSources;
                switch (result.provider) {
                  case AgroConnectExternalParty.Dacom:
                    externalCropFieldSource = ExternalCropFieldSources.Dacom;
                    break;
                  case AgroConnectExternalParty.CropVision:
                    externalCropFieldSource = ExternalCropFieldSources.CropVision;
                    break;
                  default:
                    externalCropFieldSource = ExternalCropFieldSources.Dacom;
                    break;
                }

                return of(
                  new TriggerSyncCropFieldsV2Request(
                    result.year,
                    cropFieldType,
                    externalCropFieldSource,
                    result.farmId,
                  ),
                );
              } else {
                return EMPTY;
              }
            }),
          ),
      ),
    ),
  );

  askSynchronizeCropfieldsWithRvO$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CropFieldActionTypes.AskSynchronizeCropFieldsWithRvO),
      withLatestFrom(
        this.store.pipe(select(getCropYears)),
        this.store.pipe(select(selectCustomerProgress)),
        this.store.pipe(select(getCropYear)),
      ),
      switchMap(([_, cropYears, customerProgress, cropYear]) =>
        this.dialog
          .dialog(
            RetrieveCropfieldsDialogComponent,
            {
              title: this.locale.translate('retrieveCropFieldsForRvO'),
              fullscreen: true,
              confirmButton: true,
              cancelButton: true,
              cancelButtonText: this.locale.translate('cancel'),
              providers: Object.values(RvoProvider),
              provider: RvoProvider.RvO,
              years: cropYears,
              year: customerProgress?.year ?? cropYear,
              pushHistoryState: true,
              showProviderField: false,
            },
            { maxWidth: 500 },
          )
          .pipe(
            switchMap(({ status, result }) => {
              if (status === DIALOG_RETURN_TYPES.confirm) {
                return of(
                  new TriggerSyncCropFieldsV2Request(
                    result.year,
                    CropFieldType.Advice,
                    ExternalCropFieldSources.RvO,
                    result.farmId,
                  ),
                );
              } else {
                return EMPTY;
              }
            }),
          ),
      ),
    ),
  );

  askImportRegulatoryManureFields$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CropFieldActionTypes.AskImportRegulatoryManureFields),
      withLatestFrom(
        this.store.pipe(select(getCropYears)),
        this.store.pipe(select(selectCustomerProgress)),
        this.store.pipe(select(getCropYear)),
      ),
      switchMap(([_, cropYears, customerProgress, cropYear]) =>
        this.dialog
          .dialog(
            RetrieveCropfieldsDialogComponent,
            {
              title: this.locale.translate('importRegulatoryManureFields'),
              fullscreen: true,
              confirmButton: true,
              cancelButton: true,
              cancelButtonText: this.locale.translate('cancel'),
              providers: Object.values(RvoProvider),
              provider: RvoProvider.RvORegulatoryParcelManure,
              years: cropYears,
              year: customerProgress?.year ?? cropYear,
              pushHistoryState: true,
              showProviderField: false,
            },
            { maxWidth: 500 },
          )
          .pipe(
            switchMap(({ status, result }) => {
              if (status === DIALOG_RETURN_TYPES.confirm) {
                return of(
                  new TriggerSyncCropFieldsV2Request(
                    result.year,
                    CropFieldType.ManureSpace,
                    ExternalCropFieldSources.RvORegulatoryParcelManure,
                    result.farmId,
                  ),
                );
              } else {
                return EMPTY;
              }
            }),
          ),
      ),
    ),
  );

  loadAgroConnectCustomersRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CropFieldActionTypes.LoadAgroConnectCustomersRequest),
      mergeMap((action: LoadAgroConnectCustomersRequest) =>
        this.agroConnectService.getExternalCustomers(action.provider, action.year).pipe(
          map((customers) => new LoadAgroConnectCustomersSuccess(customers)),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  askCropFieldsFromSync$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CropFieldActionTypes.AskImportCropFieldsFromSync),
      withLatestFrom(
        this.store.pipe(select(getAvailableCropYears)),
        this.store.pipe(select(getCropYear)),
      ),
      switchMap(
        ([action, availableCropYears, cropYear]: [AskImportCropFieldsFromSync, number[], number]) =>
          this.dialog
            .dialog(
              SyncCropfieldsDialogComponent,
              {
                title: isRvOProvider(action.provider)
                  ? this.locale.translate('importCropFieldsFromRvO')
                  : this.locale.translate('importCropFieldsFromBms'),
                fullscreen: true,
                confirmButton:
                  action.newCropFields.length > 0 ||
                  action.changedCropFields.newValues.length > 0 ||
                  action.deletedCropFields.length > 0,
                cancelButton: true,
                cancelButtonText: this.locale.translate('cancel'),
                newCropFields: action.newCropFields,
                changedCropFields: action.changedCropFields,
                deletedCropFields: action.deletedCropFields,
                provider: action.provider,
                years: availableCropYears,
                year: cropYear,
              },
              { maxWidth: 800 },
            )
            .pipe(
              switchMap(({ status, result }) => {
                if (status === DIALOG_RETURN_TYPES.confirm) {
                  this.appInsights.trackEvent(
                    `BmsImport.${result.cropFields[0]?.externalIdSource}`,
                    { numberOfCropFields: result.cropFields.length },
                  );
                  return of(
                    new UpsertImportedCropFieldsRequest(
                      result.cropFields,
                      result.year,
                      true,
                      action.provider,
                    ),
                  );
                }
                return EMPTY;
              }),
            ),
      ),
    ),
  );

  upsertImportedCropFieldsRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CropFieldActionTypes.UpsertImportedCropFieldsRequest),
      withLatestFrom(
        this.store.pipe(select(getSoilTypes)),
        this.store.pipe(select(getCropYear)),
        this.store.pipe(select(getCropFieldType)),
      ),
      mergeMap(
        ([action, soilTypes, cropYear, cropFieldType]: [
          UpsertImportedCropFieldsRequest,
          SoilType[],
          number,
          CropFieldType,
        ]) => {
          const failedToUpsert: FailedToSyncCropFields = [];

          return from(action.cropFields).pipe(
            mergeMap(
              (originalCropField) =>
                this.processIndividualCropField(
                  originalCropField,
                  action,
                  soilTypes,
                  cropYear,
                  cropFieldType,
                ),
              3, // Limit concurrent processing to 3
            ),
            reduce((acc: CropField[], cropField: CropField) => [...acc, cropField], []),
            switchMap((processedCropFields) =>
              this.executeUpsertRequests(
                processedCropFields,
                action,
                cropFieldType,
                failedToUpsert,
              ),
            ),
            switchMap(() => this.handleFailedUpserts(failedToUpsert)),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          );
        },
      ),
    ),
  );

  private processIndividualCropField(
    originalCropField: CropField,
    action: UpsertImportedCropFieldsRequest,
    soilTypes: SoilType[],
    cropYear: number,
    cropFieldType: CropFieldType,
  ): Observable<CropField> {
    return of(originalCropField).pipe(
      observeOn(asyncScheduler),
      map(() => this.prepareFeatureForProcessing(originalCropField, action)),
      switchMap(({ cropField, feature, extent }) =>
        this.loadLayerDataIfNeeded(cropField, feature, extent, action),
      ),
      switchMap(({ cropField, feature }) =>
        this.enrichCropFieldWithLayerData(cropField, feature, action, soilTypes, cropFieldType),
      ),
      map((cropFieldToUpsert) =>
        this.transformCropFieldForUpsert(cropFieldToUpsert, cropYear, action, cropFieldType),
      ),
    );
  }

  private prepareFeatureForProcessing(
    originalCropField: CropField,
    action: UpsertImportedCropFieldsRequest,
  ): { cropField: CropField; feature?: OlFeature<Geometry>; extent?: Extent } {
    if (!action.useLayerData || !originalCropField.feature) {
      return { cropField: originalCropField };
    }

    const reader = new GeoJSON();
    const feature = reader.readFeature(originalCropField.feature);
    const extent = feature.getGeometry().getExtent();
    return { cropField: originalCropField, feature, extent };
  }

  private loadLayerDataIfNeeded(
    cropField: CropField,
    feature: OlFeature<Geometry> | undefined,
    extent: Extent | undefined,
    action: UpsertImportedCropFieldsRequest,
  ): Observable<{ cropField: CropField; feature: OlFeature<Geometry> | null }> {
    return action.useLayerData && extent
      ? this.layerDataService
          .loadLayerFeaturesInExtent(extent)
          .pipe(map(() => ({ cropField, feature: feature || null })))
      : of({ cropField, feature: null });
  }

  private enrichCropFieldWithLayerData(
    cropField: CropField,
    feature: OlFeature<Geometry> | null,
    action: UpsertImportedCropFieldsRequest,
    soilTypes: SoilType[],
    cropFieldType: CropFieldType,
  ): Observable<CropField> {
    if (!action.useLayerData || !feature || !cropField.feature) {
      return of(
        this.applyLayerDataToCropField(cropField, feature, action, soilTypes, cropFieldType),
      );
    }
    const cropFieldToUpsert = transformedCropFields(
      [cropField],
      CoordinateSystem.PseudoMercator,
      CoordinateSystem.WorldGeodetic,
    )[0];

    // Call backend validation API
    return this.fieldService.validateGeometry({ feature: cropFieldToUpsert.feature }).pipe(
      map((validation: ValidateCropFieldGeometryResponse) => {
        // Apply subsequent cultivation naming
        if (this.shouldApplySubsequentCultivationNaming(cropFieldType, action, cropField)) {
          cropFieldToUpsert.name = this.buildSubsequentCultivationName(cropField.name);
        }

        // Apply layer data properties from backend validation
        cropFieldToUpsert.isGroundWaterPreservation = validation.isGroundWaterPreservation || false;
        cropFieldToUpsert.isSandySoil = validation.isSandySoil || false;
        cropFieldToUpsert.soilTypeId =
          validation.suggestedSoilTypeId || cropFieldToUpsert.soilTypeId;
        cropFieldToUpsert.isNutrientsPollutedArea = validation.isNutrientsPollutedArea;
        cropFieldToUpsert.isGroundWaterPreservationWithNitrogenCuts =
          this.determineGroundWaterPreservationWithNitrogenCuts(cropFieldToUpsert, soilTypes);

        // Set default soil type for RvO imports
        if (this.shouldSetDefaultRvoSoilType(cropFieldToUpsert)) {
          cropFieldToUpsert.soilTypeId = 1; // Sand
        }

        return cropFieldToUpsert;
      }),
    );
  }

  private applyLayerDataToCropField(
    cropField: CropField,
    _feature: OlFeature<Geometry>,
    action: UpsertImportedCropFieldsRequest,
    _soilTypes: SoilType[],
    cropFieldType: CropFieldType,
  ): CropField {
    // Fallback method when backend validation is not used (no feature or useLayerData=false)
    const cropFieldToUpsert = { ...cropField };

    // Apply subsequent cultivation naming
    if (this.shouldApplySubsequentCultivationNaming(cropFieldType, action, cropField)) {
      cropFieldToUpsert.name = this.buildSubsequentCultivationName(cropField.name);
    }

    // Set default soil type for RvO imports
    if (this.shouldSetDefaultRvoSoilType(cropFieldToUpsert)) {
      cropFieldToUpsert.soilTypeId = 1; // Sand
    }

    return cropFieldToUpsert;
  }

  private shouldApplySubsequentCultivationNaming(
    cropFieldType: CropFieldType,
    action: UpsertImportedCropFieldsRequest,
    cropField: CropField,
  ): boolean {
    return (
      cropFieldType === CropFieldType.ManureSpace &&
      action.externalSource === RvoProvider.RvORegulatoryParcelManure &&
      cropField.isSubsequentCultivation
    );
  }

  private buildSubsequentCultivationName(originalName: string): string {
    return originalName + ' (' + this.locale.translate('subsequentCultivation') + ')';
  }

  private determineGroundWaterPreservationWithNitrogenCuts(
    cropField: CropField,
    soilTypes: SoilType[],
  ): boolean {
    return isGroundWaterPreservationWithNitrogenCuts(
      cropField.soilTypeId,
      soilTypes,
      cropField.isGroundWaterPreservation,
      cropField.province,
    );
  }

  private shouldSetDefaultRvoSoilType(cropField: CropField): boolean {
    return !cropField.soilTypeId && isRvOProvider(cropField.externalIdSource);
  }

  private transformCropFieldForUpsert(
    cropFieldToUpsert: CropField,
    cropYear: number,
    action: UpsertImportedCropFieldsRequest,
    cropFieldType: CropFieldType,
  ): CropField {
    const transformedCropField = transformedCropFields(
      [this.setCropFieldYear(cropFieldToUpsert, cropYear, action.year)],
      CoordinateSystem.PseudoMercator,
      CoordinateSystem.WorldGeodetic,
    )[0];

    transformedCropField.cropFieldType = cropFieldType;
    return transformedCropField;
  }

  private executeUpsertRequests(
    processedCropFields: CropField[],
    action: UpsertImportedCropFieldsRequest,
    cropFieldType: CropFieldType,
    failedToUpsert: FailedToSyncCropFields,
  ): Observable<null> {
    const requests = this.buildUpsertRequests(
      processedCropFields,
      action,
      cropFieldType,
      failedToUpsert,
    );

    if (requests.length === 0) {
      return of(null);
    }

    return from(requests).pipe(
      mergeMap((request) => request, 3),
      toArray(),
      map(() => null as null),
    );
  }

  private buildUpsertRequests(
    processedCropFields: CropField[],
    action: UpsertImportedCropFieldsRequest,
    cropFieldType: CropFieldType,
    failedToUpsert: FailedToSyncCropFields,
  ): Observable<UpsertCropFieldsWithRegulatoryParcelResponse | null>[] {
    const requests: Observable<UpsertCropFieldsWithRegulatoryParcelResponse | null>[] = [];

    if (this.shouldProcessRegulatoryParcels(cropFieldType, action)) {
      this.addRegulatoryParcelRequests(processedCropFields, failedToUpsert, requests);
    } else {
      this.addIndividualCropFieldRequests(processedCropFields, failedToUpsert, requests);
    }

    return requests;
  }

  private shouldProcessRegulatoryParcels(
    cropFieldType: CropFieldType,
    action: UpsertImportedCropFieldsRequest,
  ): boolean {
    return (
      cropFieldType === CropFieldType.ManureSpace &&
      action.externalSource === RvoProvider.RvORegulatoryParcelManure
    );
  }

  private addRegulatoryParcelRequests(
    processedCropFields: CropField[],
    failedToUpsert: FailedToSyncCropFields,
    requests: Observable<UpsertCropFieldsWithRegulatoryParcelResponse | null>[],
  ): void {
    const regulatoryBatches = this.createBatches(processedCropFields, 20);

    regulatoryBatches.forEach((batch) => {
      requests.push(this.createRegulatoryParcelRequest(batch, failedToUpsert));
    });
  }

  private createRegulatoryParcelRequest(
    batch: CropField[],
    failedToUpsert: FailedToSyncCropFields,
  ): Observable<UpsertCropFieldsWithRegulatoryParcelResponse | null> {
    return this.fieldService.upsertCropFieldsWithRegulatoryParcel(batch).pipe(
      map((response) => this.handleRegulatoryParcelSuccess(response, batch, failedToUpsert)),
      catchError((error: HttpErrorResponse) =>
        this.handleRegulatoryParcelError(error, batch, failedToUpsert),
      ),
    );
  }

  private handleRegulatoryParcelSuccess(
    response: UpsertCropFieldsWithRegulatoryParcelResponse,
    batch: CropField[],
    failedToUpsert: FailedToSyncCropFields,
  ): UpsertCropFieldsWithRegulatoryParcelResponse {
    // Dispatch success actions for succeeded crop fields
    response.succeeded.forEach((success, index) => {
      const cropField = { ...batch[index], id: success.id };
      this.store.dispatch(new UpsertCropFieldSuccess(cropField, true));
    });

    // Track failed crop fields
    response.failed.forEach((failed, index) => {
      const cropField = batch.find((cf) => cf.name === failed.name);
      if (cropField) {
        failedToUpsert.push({
          cropField,
          error: new HttpErrorResponse({ error: failed.error }),
          batchIndex: index,
        });
      }
    });

    return response;
  }

  private handleRegulatoryParcelError(
    error: HttpErrorResponse,
    batch: CropField[],
    failedToUpsert: FailedToSyncCropFields,
  ): Observable<null> {
    console.error('Error upserting regulatory parcel crop fields:', error);

    batch.forEach((cropField, index) => {
      failedToUpsert.push({ cropField, error, batchIndex: index });
    });

    return of(null);
  }

  private addIndividualCropFieldRequests(
    processedCropFields: CropField[],
    failedToUpsert: FailedToSyncCropFields,
    requests: Observable<UpsertCropFieldsWithRegulatoryParcelResponse | null>[],
  ): void {
    processedCropFields.forEach((cropField) => {
      requests.push(this.createIndividualCropFieldRequest(cropField, failedToUpsert));
    });
  }

  private createIndividualCropFieldRequest(
    cropField: CropField,
    failedToUpsert: FailedToSyncCropFields,
  ): Observable<null> {
    return this.fieldService.upsertCropField(cropField).pipe(
      map((): null => {
        this.store.dispatch(new UpsertCropFieldSuccess(cropField, true));
        return null;
      }),
      catchError((error: HttpErrorResponse): Observable<null> => {
        console.error('Error upserting crop field:', error);
        failedToUpsert.push({ cropField, error, batchIndex: 0 });
        return of(null);
      }),
    );
  }

  private handleFailedUpserts(failedToUpsert: FailedToSyncCropFields): Observable<Action> {
    if (failedToUpsert.length > 0) {
      return this.dialog
        .dialog(
          SyncCropfieldsFailedDialogComponent,
          {
            title: this.locale.translate('cropFieldSyncFailedDialog.title'),
            failedCropFields: failedToUpsert,
            confirmButton: true,
            cancelButton: false,
            confirmButtonText: this.locale.translate('ok'),
          },
          { maxWidth: 800 },
        )
        .pipe(switchMap(() => EMPTY));
    }

    return of(new UpsertImportedCropFieldsSuccess());
  }

  upsertImportedCropFieldsSuccess$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CropFieldActionTypes.UpsertImportedCropFieldsSuccess),
      withLatestFrom(this.store.pipe(select(selectSoilAnalysisLoading))),
      concatMap(([_, isSoilAnalysisLoading]) => {
        const actions: Action[] = [new DeselectAllFields(), new LoadCropFieldsRequest()];
        if (!isSoilAnalysisLoading) {
          actions.push(getSoilAnalyses());
        }

        return actions;
      }),
    ),
  );

  selectCustomerForImport$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CropFieldActionTypes.SelectCustomerForImport),
        concatMap(() =>
          this.dialog
            .dialog(
              SelectCustomerDialogComponent,
              { title: this.locale.translate('selectCustomer'), pushHistoryState: false },
              { height: '100%', width: '100%', maxHeight: '65vh' },
            )
            .pipe(() => EMPTY),
        ),
      ),
    { dispatch: false },
  );

  selectCropYearForImportFromCustomer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CropFieldActionTypes.SelectCropYearForImportFromCustomer),
      withLatestFrom(this.store.pipe(select(getAvailableCropYears))),
      switchMap(([action, availableCropYears]: [SelectCropYearForImportFromCustomer, number[]]) =>
        this.dialog
          .select({
            options: availableCropYears.map((year) => ({ name: year.toString(), value: year })),
            selected: null,
            emptyText: '',
            title: this.locale.translate('selectYear'),
            pushHistoryState: false,
          })
          .pipe(
            switchMap(({ status, result }) => {
              if (status === DIALOG_RETURN_TYPES.confirm && result) {
                return of(new RetrieveCropFieldsFromCustomerForImport(action.customerId, result));
              }
              return EMPTY;
            }),
          ),
      ),
    ),
  );

  retrieveCropFieldsFromCustomerForImport$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CropFieldActionTypes.RetrieveCropFieldsFromCustomerForImport),
      withLatestFrom(this.store.pipe(select(getCropFieldType))),
      switchMap(
        ([action, cropFieldType]: [RetrieveCropFieldsFromCustomerForImport, CropFieldType]) =>
          this.fieldService
            .getCropFieldsForCustomer(action.cropYear, cropFieldType, action.customerId)
            .pipe(
              catchError((error: HttpErrorResponse) =>
                error.status === 403
                  ? this.dialog
                      .notify({
                        title: this.locale.translate('noAccess'),
                        text: this.locale.translate('noAccessCustomer'),
                      })
                      .pipe(() => EMPTY)
                  : throwError(error),
              ),
              map(
                (cropFields) =>
                  new LoadCropFieldsFromCustomerSuccess(
                    transformedCropFields(
                      cropFields.map(
                        (cropField) => ({ ...cropField, id: 0, soilAnalysisIds: [] }) as CropField,
                      ),
                      CoordinateSystem.WorldGeodetic,
                      CoordinateSystem.PseudoMercator,
                    ),
                    action.cropYear,
                  ),
              ),
              startWith(CoreActions.setLoading({ loading: true })),
              finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
            ),
      ),
    ),
  );

  loadCropFieldsFromCustomerSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CropFieldActionTypes.LoadCropFieldsFromCustomerSuccess),
      switchMap((action: LoadCropFieldsFromCustomerSuccess) => {
        if (action.cropFields.length === 0) {
          return this.dialog
            .notify({
              title: this.locale.translate('retrieveCropFields'),
              text: this.locale.translate('customerHasNoCropFields'),
            })
            .pipe(switchMap(() => EMPTY));
        }

        return of(new SelectCropFieldsForImportFromCustomer(action.cropFields, action.year));
      }),
    ),
  );

  selectCropFieldsForImportFromCustomer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CropFieldActionTypes.SelectCropFieldsForImportFromCustomer),
      switchMap((action: SelectCropFieldsForImportFromCustomer) =>
        this.dialog
          .dialog(
            ImportCropfieldsDialogComponent,
            {
              title: this.locale.translate('importCropFieldsFromCustomer'),
              fullscreen: true,
              confirmButton: action.cropFields.length > 0,
              cancelButton: true,
              cancelButtonText: this.locale.translate('cancel'),
              cropFields: action.cropFields,
              years: [],
              year: action.year,
              pushHistoryState: false,
            },
            { maxWidth: 500 },
          )
          .pipe(
            switchMap(({ status, result }) => {
              if (status === DIALOG_RETURN_TYPES.confirm) {
                return of(
                  new UpsertImportedCropFieldsRequest(result.cropFields, result.year, false, null),
                );
              }
              return EMPTY;
            }),
          ),
      ),
    ),
  );

  loadRvOCropFieldsSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CropFieldActionTypes.LoadRvOCropFieldsSuccess),
        switchMap((action: LoadRvOCropFieldsSuccess) => {
          if (!action.rvoCropFields) {
            return EMPTY;
          }
          if (action.rvoCropFields.length === 0) {
            this.dialog.notify({
              title: this.locale.translate('retrieveCropFields'),
              text: this.locale.translate('customerHasNoCropFields'),
            });
            return EMPTY;
          }
          this.store.dispatch(CoreActions.setLoading({ loading: true }));

          const cropFields$ = this.convertAgroConnectCropFields(
            RvoProvider.RvO,
            action.rvoCropFields,
          );

          cropFields$.subscribe((cropFields) => {
            this.store.dispatch(CoreActions.setLoading({ loading: false }));
            this.store.dispatch(new AskImportCropFieldsForRvO(cropFields));
          });
          return EMPTY;
        }),
      ),
    { dispatch: false },
  );

  askImportCropFieldsForRvO$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CropFieldActionTypes.AskImportCropFieldsForRvO),
      withLatestFrom(
        this.store.pipe(select(getAvailableCropYears)),
        this.store.pipe(select(getDefaultCropYear)),
      ),
      switchMap(
        ([action, availableCropYears, defaultCropYear]: [AskImportCropFields, number[], number]) =>
          this.dialog
            .dialog(
              ImportCropfieldsDialogComponent,
              {
                title: this.locale.translate('importCropFieldsFromRvO'),
                fullscreen: true,
                confirmButton: action.cropFields.length > 0,
                cancelButton: true,
                cancelButtonText: this.locale.translate('cancel'),
                cropFields: action.cropFields,
                years: availableCropYears,
                year: defaultCropYear,
              },
              { maxWidth: 500 },
            )
            .pipe(
              switchMap(({ status, result }) => {
                if (status === DIALOG_RETURN_TYPES.confirm) {
                  this.appInsights.trackEvent(
                    `RvOImport.${action.cropFields[0].externalIdSource}`,
                    { numberOfCropFields: result.cropFields.length },
                  );
                  return of(
                    new UpsertImportedCropFieldsRequest(
                      result.cropFields,
                      result.year,
                      true,
                      RvoProvider.RvO,
                    ),
                  );
                }
                return EMPTY;
              }),
            ),
      ),
    ),
  );

  // V2 Sync Effects

  triggerSyncCropFieldsV2$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CropFieldActionTypes.TriggerSyncCropFieldsV2Request),
      withLatestFrom(this.store.pipe(select(getCropYear))),
      mergeMap(([action, cropYear]: [TriggerSyncCropFieldsV2Request, number]) => {
        const request = {
          searchYear: action.searchYear,
          cropFieldType: action.cropFieldType,
          externalCropFieldSource: action.externalCropFieldSource as ExternalCropFieldSources,
          externalId: action.externalId,
        };
        return this.syncService.triggerSyncCropFields(cropYear, request).pipe(
          map(
            () =>
              new SyncCropFieldsReadySubmitted(
                action.searchYear,
                action.externalId,
                action.externalCropFieldSource as ExternalCropFieldSources,
                true,
                null,
              ),
          ),
          catchError((error: HttpErrorResponse) => {
            console.error('Error triggering sync crop fields V2:', error);
            return of(new TriggerSyncCropFieldsV2Failed(error.message || 'Failed to trigger sync'));
          }),
          startWith(CoreActions.setLoading({ loading: true })),
        );
      }),
    ),
  );

  triggerSyncCropFieldsV2Submitted$: Observable<Action> = createEffect(() => {
    const timeoutValue = 120_000; // 120 seconds
    const syncId = new Date();

    return this.actions$.pipe(
      ofType(CropFieldActionTypes.SyncCropFieldsReadySubmitted),
      switchMap((action: SyncCropFieldsReadySubmitted) => {
        this.store.dispatch(new IsAwaitingCropFieldsSync(true));
        this.store.dispatch(new SetCropFieldsSyncId(syncId));
        return timer(timeoutValue).pipe(
          map(() => {
            return new SyncCropFieldsReadyReceived(
              action.searchYear,
              action.externalId,
              action.externalCropFieldSource as ExternalCropFieldSources,
              true,
              null,
              syncId,
            );
          }),
          takeUntil(this.actions$.pipe(ofType(CropFieldActionTypes.SyncCropFieldsReadyReceived))),
        );
      }),
    );
  });

  triggerSyncCropFieldsV2Failed$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CropFieldActionTypes.TriggerSyncCropFieldsV2Failed),
      switchMap((action: TriggerSyncCropFieldsV2Failed) =>
        this.dialog
          .notify({
            title: this.locale.translate('syncCropFields.errorTitle'),
            text: this.locale.translate('syncCropFields.errorTriggerDescription', {
              error: action.error,
            }),
          })
          .pipe(
            concatMap(() => EMPTY),
            startWith(CoreActions.setLoading({ loading: false })),
          ),
      ),
    ),
  );

  loadCropFieldsSyncPreviewV2$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CropFieldActionTypes.SyncCropFieldsReadyReceived),
      withLatestFrom(this.store.pipe(select(isAwaitingSync)), this.store.pipe(select(getSyncId))),
      mergeMap(
        ([action, isAwaitingSync, latestSyncId]: [SyncCropFieldsReadyReceived, boolean, Date]) => {
          if (action.syncId != null && action.syncId !== latestSyncId) {
            return EMPTY;
          }

          if (!isAwaitingSync) {
            return EMPTY;
          }

          // Stops the loading spinner from the trigger action
          this.store.dispatch(CoreActions.setLoading({ loading: false }));

          if (!action.succeeded) {
            const translatedErrorCodes =
              action.errorCode && action.errorCode.length > 0
                ? action.errorCode.map((code) =>
                    this.locale.translate(`syncCropFields.errorCodes.${code}`),
                  )
                : null;
            this.dialog.notify({
              title: this.locale.translate('syncCropFields.errorTitle'),
              text: translatedErrorCodes
                ? translatedErrorCodes.join(', ')
                : this.locale.translate('syncCropFields.errorCodes.GenericError'),
            });
            return EMPTY;
          }

          return this.syncService
            .getCropFieldSyncPreview(action.externalCropFieldSource, action.externalId)
            .pipe(
              map(
                (preview) =>
                  new LoadCropFieldsSyncPreviewV2Success(
                    preview,
                    action.externalCropFieldSource,
                    action.externalId,
                  ),
              ),
              catchError((_: HttpErrorResponse) => {
                this.store.dispatch(CoreActions.setLoading({ loading: false }));

                // 404 means sync is still processing or failed, show retry/cancel dialog
                return this.dialog
                  .confirm({
                    title: this.locale.translate('syncCropFields.errorTitle'),
                    text: this.locale.translate('syncCropFields.noDataFound'),
                    confirmButtonText: this.locale.translate('retry'),
                    cancelButtonText: this.locale.translate('cancel'),
                  })
                  .pipe(
                    switchMap(({ status }) => {
                      if (status === DIALOG_RETURN_TYPES.confirm) {
                        this.store.dispatch(CoreActions.setLoading({ loading: true }));
                        if (
                          action.externalCropFieldSource ==
                          ExternalCropFieldSources.RvORegulatoryParcelManure
                        ) {
                          return of(new AskImportRegulatoryManureFields());
                        }
                        if (action.externalCropFieldSource == ExternalCropFieldSources.RvO) {
                          return of(new AskSynchronizeCropFieldsRvO());
                        }

                        return of(new AskSynchronizeCropFields());
                      } else {
                        return EMPTY;
                      }
                    }),
                  );
              }),
              startWith(CoreActions.setLoading({ loading: true })),
              finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
            );
        },
      ),
    ),
  );

  loadCropFieldsSyncPreviewV2Success$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CropFieldActionTypes.LoadCropFieldsSyncPreviewV2Success),
      withLatestFrom(
        this.store.pipe(select(getAvailableCropYears)),
        this.store.pipe(select(getCropYear)),
      ),
      switchMap(
        ([action, availableCropYears, cropYear]: [
          LoadCropFieldsSyncPreviewV2Success,
          number[],
          number,
        ]) => {
          const titleTranslationCode = action.externalCropFieldSource.includes('RvO')
            ? 'synchronizeCropFieldsWithRvO'
            : 'synchronizeCropFieldsWithBMS';
          return this.dialog
            .dialog(
              SyncCropfieldsDialogV2Component,
              {
                title: this.locale.translate(titleTranslationCode),
                fullscreen: true,
                confirmButton: true,
                cancelButton: true,
                cancelButtonText: this.locale.translate('cancel'),
                syncData: action.syncData,
                externalCropFieldSource: action.externalCropFieldSource,
                externalId: action.externalId,
                years: availableCropYears,
                year: cropYear,
              },
              { maxWidth: 800 },
            )
            .pipe(
              switchMap(({ status, result }) => {
                if (status === DIALOG_RETURN_TYPES.confirm) {
                  const syncResult = result as {
                    selectedNewExternalIds: string[];
                    selectedChangedExternalIds: string[];
                  };
                  this.appInsights.trackEvent(
                    `SyncCropFieldsV2Complete.${action.externalCropFieldSource}`,
                    {
                      selectedNewCount: syncResult.selectedNewExternalIds.length,
                      selectedChangedCount: syncResult.selectedChangedExternalIds.length,
                    },
                  );

                  return of(
                    new CompleteSyncCropFieldsV2Request(
                      cropYear,
                      action.externalCropFieldSource,
                      action.externalId,
                      syncResult.selectedNewExternalIds,
                      syncResult.selectedChangedExternalIds,
                    ),
                  );
                }
                return EMPTY;
              }),
            );
        },
      ),
    ),
  );

  completeSyncCropFieldsV2$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CropFieldActionTypes.CompleteSyncCropFieldsV2Request),
      withLatestFrom(this.store.pipe(select(getCropFieldType))),
      mergeMap(([action, cropFieldType]: [CompleteSyncCropFieldsV2Request, CropFieldType]) => {
        const request = {
          selectedNewExternalIds: action.selectedNewExternalIds,
          selectedChangedExternalIds: action.selectedChangedExternalIds,
          cropFieldType: cropFieldType,
          externalCropFieldSource: action.externalCropFieldSource,
          externalId: action.externalId,
        };
        return this.syncService.completeSyncCropFields(action.cropYear, request).pipe(
          concatMap(() => [new LoadCropFieldsRequest(), new DeselectAllFields()]),
          catchError((error: HttpErrorResponse) => {
            console.error('Error completing sync crop fields V2:', error);
            this.dialog.notify({
              title: this.locale.translate('syncCropFields.errorTitle'),
              text: this.locale.translate('syncCropFields.errorCodes.IntegrationServiceFailure'),
            });
            return EMPTY;
          }),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        );
      }),
    ),
  );

  private convertAgroConnectCropFields(
    provider: string,
    agroConnectCropFields: AgroConnectCropField[],
    cropFieldType: CropFieldType = CropFieldType.Advice,
  ): Observable<CropField[]> {
    if (agroConnectCropFields.length === 0) {
      return of([]);
    }
    const cropFieldsWorldGeodetic = agroConnectCropFields.map(
      (agroConnectCropField) =>
        ({
          id: agroConnectCropField.id ? agroConnectCropField.id : 0,
          name: agroConnectCropField.name,
          cropId: agroConnectCropField.cropId,
          cropVarietyId: agroConnectCropField.cropVarietyId,
          cropFieldType,
          soilTypeId: agroConnectCropField.soilTypeId,
          areaInHectares: agroConnectCropField.hectares,
          isSkal: false,
          isPlanetProof: false,
          isGroundWaterPreservation: false,
          isSandySoil: false,
          isRentedField: false,
          startDate: agroConnectCropField.beginDate,
          endDate: agroConnectCropField.endDate,
          machiningDepthInCentimeters: 25,
          isSubsequentCultivation: agroConnectCropField.isSubsequentCultivation || false,
          feature: this.createFeature(agroConnectCropField),
          isAreaCalculated: false,
          soilAnalysisIds: [],
          externalIdSource: provider.toString(),
          externalId: agroConnectCropField.thirdPartyId,
          outsideDutchBorders: false,
          isGroundWaterPreservationWithNitrogenCuts: false,
          isNutrientsPollutedArea: false,
          palValueFrom2021: agroConnectCropField.palValueFrom2021 || null,
          pcacl2Value: agroConnectCropField.pcacl2Value || null,
        }) as CropField,
    );

    const cropFieldsPseudoMercator = transformedCropFields(
      cropFieldsWorldGeodetic,
      CoordinateSystem.WorldGeodetic,
      CoordinateSystem.PseudoMercator,
    );

    const cropFields$ = this.provinceService
      .getProvincesFromFeatures(
        cropFieldsPseudoMercator.map((cropFieldPseudoMercator) => cropFieldPseudoMercator.feature),
      )
      .pipe(
        map((provinceMatches) => {
          if (provinceMatches.length === cropFieldsPseudoMercator.length) {
            const cropFieldsWithProvince: CropField[] = [];
            cropFieldsPseudoMercator.forEach((cropField, index) => {
              const provinceMatch = provinceMatches[index];
              cropFieldsWithProvince.push({
                ...cropField,
                province: provinceMatch ? provinceMatch[0] : null,
                outsideDutchBorders: !provinceMatch,
              } as CropField);
            });

            return cropFieldsWithProvince;
          } else {
            return cropFieldsPseudoMercator;
          }
        }),
      );

    return cropFields$;
  }

  private createFeature = (agroConnectCropField: AgroConnectCropField): Feature =>
    ({
      id: '',
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [agroConnectCropField.coordinates],
      },
      properties: {},
    }) as Feature;

  private setCropFieldYear(cropField: CropField, year: number, importYear: number) {
    const startDate = new Date(cropField.startDate);
    const endDate = new Date(cropField.endDate);

    if (startDate.getFullYear() === endDate.getFullYear()) {
      startDate.setFullYear(year);
      endDate.setFullYear(year);

      return { ...cropField, startDate: startDate.toISOString(), endDate: endDate.toISOString() };
    }

    // If the end date year is the same as the import year, then the start date is in the previous year.
    const isStartDateInPreviousYear = endDate.getFullYear() === importYear;

    if (!isStartDateInPreviousYear && cropField.externalIdSource === ExternalSource.rvo) {
      startDate.setFullYear(year - 1);
      endDate.setFullYear(year);

      return { ...cropField, startDate: startDate.toISOString(), endDate: endDate.toISOString() };
    }

    // Based on if started date is in the previous year, decide the correct start and end date years.
    const startDateYear = isStartDateInPreviousYear ? year - 1 : year;
    const endDateYear = isStartDateInPreviousYear ? year : year + 1;

    startDate.setFullYear(startDateYear);
    endDate.setFullYear(endDateYear);

    return { ...cropField, startDate: startDate.toISOString(), endDate: endDate.toISOString() };
  }

  private combineCropFieldProperties(
    field1: CropField,
    field2: CropField,
  ): Partial<CropFieldProperties> {
    const properties: Partial<CropField> = {};

    const propertiesToSkip = [
      'id',
      'feature',
      'isAreaCalculated',
      'cropId',
      'cropVarietyId',
      'startDate',
      'endDate',
      'name',
      'areaInHectares',
      'layerData',
      'cropFieldType',
    ];

    this.getPropertyNames(field1).forEach((propertyName) => {
      if (propertiesToSkip.indexOf(propertyName) === -1) {
        if (
          this.getPropertyValue(field1, propertyName) ===
          this.getPropertyValue(field2, propertyName)
        ) {
          this.setProperty(properties, propertyName, this.getPropertyValue(field1, propertyName));
        }
      }
    });

    properties.soilAnalysisIds = !field1
      ? []
      : field1.soilAnalysisIds
          .concat(field2.soilAnalysisIds)
          .filter((item, index, self) => self.indexOf(item) === index);

    return properties as CropFieldProperties;
  }

  private getPropertyValue<T, K extends keyof T>(o: T, propertyName: K): T[K] {
    return o[propertyName];
  }

  private getPropertyNames<T, K extends keyof T>(o: T): K[] {
    return Object.keys(o) as K[];
  }

  private setProperty<T, K extends keyof T>(o: T, propertyName: K, propertyValue: T[K]) {
    o[propertyName] = propertyValue;
  }

  private createBatches<T>(array: T[], batchSize: number): T[][] {
    const batches: T[][] = [];
    for (let i = 0; i < array.length; i += batchSize) {
      batches.push(array.slice(i, i + batchSize));
    }
    return batches;
  }
}
